import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import {
  FlagRounded,
  ArrowDropUpRounded,
  ArrowDropDownRounded,
} from '@mui/icons-material';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { LANGUAGE, MAX_AUDIO_RETENTION_PERIOD } from '@src/constants';
import { Paper, Popper, Typography, ClickAwayListener } from '@mui/material';
import i18n from '@src/languages';
import { REQUEST_STATUS } from '@src/constants/voice';
import { useSelector } from 'react-redux';
import { LANDING_PAGE_URL } from '@src/configs';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import PlayAudioButton from './PlayAudioButton';
import EffectGuideButton from './EffectGuideButton';
import { StyledSuccessButton, StyledMenu, StyledTextIcon } from './index.style';
import { StyledIconButton } from '../NewRequest/index.style';
import ActionDownloadButtons from '../TitleBar/ActionDownloadButton';

const SuccessButton = ({
  useNewUXSynthesis,
  request = {},
  audioRef,
  setCloseMoreMenu,
}) => {
  const {
    id: requestId,
    title,
    audioLink,
    retentionPeriod = MAX_AUDIO_RETENTION_PERIOD,
    createdAt,
    status,
    voice,
  } = request;
  const { user } = useSelector((state) => state.auth);

  const audioExpired = moment().isAfter(
    moment(createdAt).add(retentionPeriod, 'days'),
  );
  const isDisabledAudio = !audioLink || audioExpired;

  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);

  const handleClosePopup = () => setAnchorEl(null);

  const { t } = useTranslation();
  const { language } = i18n;

  const refClickOutSide = useClickOutside(() => {
    setAnchorEl(null);
  });
  const handleOpenErrorReport = (e) => {
    e.stopPropagation();
    handleClosePopup();
    const ticketMetadata = {
      requestId,
      voiceCode: voice.code,
      userId: user.id,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.TTS}`,
      '_blank',
    );
  };

  const handleOpenListAction = () => {
    if (anchorEl) {
      setAnchorEl(null);
      return;
    }
    setAnchorEl(buttonRef.current);
  };

  const handleCloseMoreMenu = () => {
    setCloseMoreMenu(true);
  };

  const getTitleActionTooltip = (action) => {
    if (status !== REQUEST_STATUS.SUCCESS) return t('');
    if (audioExpired) return t('notiAudioExpired', { retentionPeriod });

    return t(action);
  };

  return (
    <div ref={refClickOutSide}>
      <StyledSuccessButton
        useNewUXSynthesis={useNewUXSynthesis}
        langEng={language !== LANGUAGE.VN}
        ref={buttonRef}
      >
        {useNewUXSynthesis ? (
          <ActionDownloadButtons request={request} audioRef={audioRef} />
        ) : (
          <PlayAudioButton
            onClick={handleClosePopup}
            audioRef={audioRef}
            request={request}
            langEng={language !== LANGUAGE.VN}
            hasDisable={isDisabledAudio}
            getTitleActionTooltip={getTitleActionTooltip}
          />
        )}

        <div
          className="arrow-button default-background"
          data-id="view-other-curr-req-actions"
          onClick={handleOpenListAction}
          role="button"
          tabIndex="0"
        >
          {anchorEl ? (
            <ArrowDropUpRounded
              className="option-button"
              onClick={handleCloseMoreMenu}
              sx={{ fontSize: '26px' }}
            />
          ) : (
            <ArrowDropDownRounded
              className="option-button"
              onClick={handleCloseMoreMenu}
              sx={{ fontSize: '26px' }}
            />
          )}
        </div>
        {anchorEl && (
          <ClickAwayListener onClickAway={handleClosePopup}>
            <Popper
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              style={{
                zIndex: 11,
                background: 'white',
                left: language !== LANGUAGE.VN ? '-48px' : '-5px',
              }}
              disablePortal
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5],
                  },
                },
              ]}
            >
              <Paper>
                <StyledMenu engButton={language !== LANGUAGE.VN}>
                  <EffectGuideButton
                    hasDisable={isDisabledAudio}
                    onClick={handleClosePopup}
                    requestId={requestId}
                    audioLink={audioLink}
                    title={title}
                    langEng={language !== LANGUAGE.VN}
                  />
                  <StyledTextIcon
                    disableRipple
                    data-id="report-curr-req"
                    data-request-id={request.id}
                    onClick={handleOpenErrorReport}
                    langEng={language !== LANGUAGE.VN}
                  >
                    <StyledIconButton
                      disabled={!audioLink}
                      height="40px"
                      width="40px"
                      padding="12px"
                      isDisableHover
                    >
                      <FlagRounded
                        style={{ color: '#FF3B30', fontSize: '24px' }}
                      />
                    </StyledIconButton>

                    <Typography className="text-action">
                      {t('errorReport')}
                    </Typography>
                  </StyledTextIcon>
                </StyledMenu>
              </Paper>
            </Popper>
          </ClickAwayListener>
        )}
      </StyledSuccessButton>
    </div>
  );
};

export default SuccessButton;
